import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { CompanyGroupListSort, type CompanyGroupResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Pagination,
  Panel,
  type Column,
  type ColumnFiltersState,
  type SortingState,
} from '../../components';
import { formatDate, formatInteger } from '../../format';
import { PATHS, staffGroupPath } from '../../routes/paths';
import { useGroupsPage, type GroupsPageView } from '../queries';
import { GROUPS_COPY } from './groups';
import { pageFrom, SEARCH_DEBOUNCE_MS } from './listAddress';

/**
 * Which economic groups exist, and how many companies each holds. Nothing else.
 *
 * The same split as the companies and the insurers: the screen a menu entry
 * leads to answers one question, and registering a group is a different one,
 * at [`/equipe/grupos/novo`](./NewGroupPage.tsx) behind the action in the
 * header. Which companies are in a group, and changing that, is the group's own
 * page -- see [`GroupDetailPage`](./GroupDetailPage.tsx).
 *
 * A row is a link (`DataTable`'s `rowLink`), as on the companies list: the name
 * is the anchor, and the rest of the line takes the same click.
 *
 * One page at a time (#440), through `useGroupsPage` rather than the
 * whole-registry `useGroups()` the pickers elsewhere rely on. Everything the
 * page shows is in the address, each value left out at its default: the column
 * filters as `?nome=` and `?identificador=`, the sort as `?ordem=` (and
 * `?sentido=desc`), and the page as `?pagina=`, 1-based. The table runs in
 * manual mode -- it never filters or sorts the page it was given, it only
 * reports what was asked for, which goes into the address and from there to
 * `GET /groups`. Typing in a column filter is debounced and replaces the
 * history entry; any new filter or sort starts again on the first page. A
 * column filter that matches nothing keeps the table and its filter row, as on
 * the companies list (#439), so the input being typed in stays where it is.
 *
 * As on the companies list, there is no free-text search box above the table
 * any more (#447): both columns it searched have a filter of their own. An old
 * link's `?busca=` is ignored, and dropped by the next address this screen
 * writes.
 *
 * There is no way to delete a group from here, or from anywhere: the API has
 * none, and a screen that offered one would be offering a refusal.
 */
export function GroupsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = viewFrom(searchParams);
  const groups = useGroupsPage(view);

  // What is typed in the column filters, which runs ahead of the address by
  // the debounce.
  const typed = textsOf(view);
  const [draft, setDraft] = useState(typed);
  // The texts this screen last wrote to the address. A change that is not
  // this one came from outside -- back, forward, the clear button -- and the
  // inputs follow it; one that is, is the inputs' own text arriving, and
  // copying it back would undo whatever was typed during the debounce.
  const written = useRef(typed);
  const typedKey = textsKey(typed);
  useEffect(() => {
    if (typedKey !== textsKey(written.current)) {
      const next = textsFrom(typedKey);
      written.current = next;
      setDraft(next);
    }
  }, [typedKey]);

  const draftKey = textsKey(trimmed(draft));
  useEffect(() => {
    if (draftKey === typedKey) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const next = textsFrom(draftKey);
      written.current = next;
      // Replace rather than push: every pause while typing is not a place
      // somebody wants Voltar to step back through.
      setSearchParams(groupsAddress({ ...viewFrom(searchParams), ...next, page: 1 }), {
        replace: true,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftKey, typedKey, searchParams, setSearchParams]);

  const goToPage = (next: number) => setSearchParams(groupsAddress({ ...view, page: next }));
  const clearFilters = () =>
    setSearchParams(groupsAddress({ ...view, name: '', slug: '', page: 1 }));

  const sorting: SortingState =
    view.sort === null ? [] : [{ id: view.sort.column, desc: view.sort.desc }];
  const onSortingChange = (next: SortingState) => {
    const entry = next[0];
    const column = entry === undefined ? undefined : sortColumn(entry.id);
    setSearchParams(
      groupsAddress({
        ...view,
        sort: entry === undefined || column === undefined ? null : { column, desc: entry.desc },
        page: 1,
      }),
    );
  };

  const columnFiltering = view.name !== '' || view.slug !== '';
  // Whether the rows on screen answer column filters, as on the companies
  // list. While the next page loads the rows are still the previous request's
  // (`keepPreviousData`), so this holds the previous answer until the new one
  // arrives: erasing a filter that matched nothing keeps the empty table and
  // its filter row, marked busy, instead of flashing the empty-registry state
  // and taking the focused input away. Set during render, React's pattern for
  // state that follows a prop, so no frame shows the stale value.
  const [rowsFiltered, setRowsFiltered] = useState(columnFiltering);
  if (groups.isSuccess && !groups.isPlaceholderData && rowsFiltered !== columnFiltering) {
    setRowsFiltered(columnFiltering);
  }

  const columnFilters: ColumnFiltersState = [
    ...(draft.name === '' ? [] : [{ id: 'name', value: draft.name }]),
    ...(draft.slug === '' ? [] : [{ id: 'slug', value: draft.slug }]),
  ];
  const onColumnFiltersChange = (next: ColumnFiltersState) => {
    const value = (id: string) => next.find((entry) => entry.id === id)?.value ?? '';
    setDraft({ name: value('name'), slug: value('slug') });
  };

  // The accessors are there because a sortable or filterable column needs
  // one; in manual mode the table never reads them to reorder or drop a row.
  const columns: readonly Column<CompanyGroupResponse>[] = [
    {
      key: CompanyGroupListSort.NAME,
      header: GROUPS_COPY.nameLabel,
      render: (row) => row.name,
      accessor: (row) => row.name,
      sortable: true,
      filter: { kind: 'text' },
    },
    {
      key: CompanyGroupListSort.SLUG,
      header: GROUPS_COPY.slugLabel,
      render: (row) => <code>{row.slug}</code>,
      accessor: (row) => row.slug,
      sortable: true,
      filter: { kind: 'text' },
    },
    {
      key: CompanyGroupListSort.MEMBER_COUNT,
      header: GROUPS_COPY.memberCountLabel,
      align: 'end',
      render: (row) => formatInteger(row.member_count),
      accessor: (row) => row.member_count,
      sortable: true,
    },
    {
      key: CompanyGroupListSort.CREATED_AT,
      header: GROUPS_COPY.createdAtLabel,
      align: 'end',
      render: (row) => formatDate(row.created_at),
      accessor: (row) => row.created_at,
      sortable: true,
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={GROUPS_COPY.title}
        description={GROUPS_COPY.description}
        actions={
          <Link className="button button--primary" to={PATHS.staffGroupNew}>
            {GROUPS_COPY.register}
          </Link>
        }
      />

      <Panel title={GROUPS_COPY.listTitle} description={GROUPS_COPY.listDescription}>
        <AsyncSection
          state={groups}
          loadingLabel={GROUPS_COPY.loading}
          errorTitle={GROUPS_COPY.error}
          // Two different empties, told apart: a registry with nothing in it,
          // and a page past the end of a list that does have groups. A column
          // filter that matches nothing is the third, and it is the table's:
          // the filter row stays on screen, where the filter that emptied it
          // can be changed.
          isEmpty={(data) => data.items.length === 0 && !(rowsFiltered && data.count === 0)}
          {...emptyState(view, groups.data?.count ?? 0, goToPage)}
        >
          {(data) => (
            <div aria-busy={groups.isPlaceholderData}>
              <DataTable
                id="groups"
                caption={GROUPS_COPY.caption}
                columns={columns}
                rows={data.items}
                rowKey={(row) => row.id}
                // Opening a group is the only thing a row does, so the row is
                // the link, exactly as on the companies list.
                rowLink={(row) => staffGroupPath(row.id)}
                manual
                sorting={sorting}
                onSortingChange={onSortingChange}
                columnFilters={columnFilters}
                onColumnFiltersChange={onColumnFiltersChange}
                filtered={rowsFiltered}
                noMatchTitle={GROUPS_COPY.noMatchingFilteredGroups}
                noMatchDescription={GROUPS_COPY.noMatchingFilteredGroupsDescription}
                noMatchAction={
                  <button type="button" className="button button--secondary" onClick={clearFilters}>
                    {GROUPS_COPY.clearGroupsFilters}
                  </button>
                }
              />
              <Pagination
                page={view.page}
                pageSize={data.limit ?? data.items.length}
                total={data.count}
                onPageChange={goToPage}
              />
            </div>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/** The free text of the view: what the filter row holds. */
interface Texts {
  readonly name: string;
  readonly slug: string;
}

function textsOf(view: GroupsPageView): Texts {
  return { name: view.name, slug: view.slug };
}

function trimmed(texts: Texts): Texts {
  return { name: texts.name.trim(), slug: texts.slug.trim() };
}

/** One string per set of texts, so effects can compare them by value. */
function textsKey(texts: Texts): string {
  return JSON.stringify([texts.name, texts.slug]);
}

function textsFrom(key: string): Texts {
  const [name = '', slug = ''] = JSON.parse(key) as string[];
  return { name, slug };
}

/**
 * `?ordem=` in the address's words. Portuguese, like `nome` and `pagina`,
 * rather than the API's column names, and the companies list's own words
 * (#439) wherever the two share a column -- `cadastrada` included, so a link
 * sorted by registration reads the same on both screens. `empresas` is this
 * list's alone.
 */
const SORT_PARAMS: Readonly<Record<CompanyGroupListSort, string>> = {
  [CompanyGroupListSort.NAME]: 'nome',
  [CompanyGroupListSort.SLUG]: 'identificador',
  [CompanyGroupListSort.MEMBER_COUNT]: 'empresas',
  [CompanyGroupListSort.CREATED_AT]: 'cadastrada',
};

function sortColumn(id: string): CompanyGroupListSort | undefined {
  return Object.values(CompanyGroupListSort).find((column) => column === id);
}

function sortFromParam(value: string | null): CompanyGroupListSort | undefined {
  return Object.values(CompanyGroupListSort).find((column) => SORT_PARAMS[column] === value);
}

/** The view an address asks for; anything it does not name is the default. */
function viewFrom(params: URLSearchParams): GroupsPageView {
  const column = sortFromParam(params.get('ordem'));
  return {
    name: (params.get('nome') ?? '').trim(),
    slug: (params.get('identificador') ?? '').trim(),
    sort: column === undefined ? null : { column, desc: params.get('sentido') === 'desc' },
    page: pageFrom(params.get('pagina')),
  };
}

/** The list's query string, with each value left out at its default. */
function groupsAddress(view: GroupsPageView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.name !== '') {
    params.set('nome', view.name);
  }
  if (view.slug !== '') {
    params.set('identificador', view.slug);
  }
  if (view.sort !== null) {
    params.set('ordem', SORT_PARAMS[view.sort.column]);
    if (view.sort.desc) {
      params.set('sentido', 'desc');
    }
  }
  if (view.page > 1) {
    params.set('pagina', String(view.page));
  }
  return params;
}

function emptyState(view: GroupsPageView, count: number, goToPage: (page: number) => void) {
  if (count > 0 && view.page > 1) {
    return {
      emptyTitle: GROUPS_COPY.groupsPageOutOfRange,
      emptyDescription: GROUPS_COPY.groupsPageOutOfRangeDescription,
      emptyAction: (
        <button type="button" className="button button--secondary" onClick={() => goToPage(1)}>
          {GROUPS_COPY.groupsFirstPage}
        </button>
      ),
    };
  }
  return {
    emptyTitle: GROUPS_COPY.none,
    emptyDescription: GROUPS_COPY.noneDescription,
  };
}
